/**
 * Stand-in for the survey endpoints, for demoing the screens without the BFF.
 *
 * **It is off by default and there is nothing to commit to turn it off.** With no opt-in, every
 * call goes to the real service, exactly as in production. It is turned on per browser tab, from
 * the URL:
 *
 * | URL | What the screens show |
 * |---|---|
 * | *(nothing)* | the real BFF |
 * | `?surveyMock=seeded` | `satisfacao-mdm` and `results.json`, verbatim |
 * | `?surveyMock=empty` | no surveys — the empty states |
 * | `?surveyMock=error` | every call rejects — the error states |
 * | `?surveyMock=off` | back to the real BFF |
 *
 * The choice sticks in `sessionStorage` for that tab, so navigating between the screens keeps it,
 * and closing the tab forgets it. Nothing here can leak into a build: no env var, no flag file.
 *
 * The payloads come from the frozen fixtures in the BFF repo
 * (`docs/features/surveys-optin/03-fixtures/survey-completo.json` and `results.json`), reshaped
 * into the admin view the panel actually receives. The store is in memory: writes survive
 * navigation and disappear on reload.
 *
 * **This module is meant to be deleted** once nobody needs the fixtures any more: remove this
 * file and the one `isSurveyMockEnabled()` line at the top of each function in
 * `Surveys.service.ts`. Nothing else references it.
 *
 * @module Pages/BUs/Surveys
 */

import {
  SurveySlugHeldByDeletedError,
  SurveyTriggerLockedError,
  SurveyVersionLockedError,
} from './Surveys.errors';
import type {
  SaveQuestionsPayload,
  Survey,
  SurveyComment,
  SurveyCommentsPage,
  SurveyCommentsRequest,
  SurveyCreatePayload,
  SurveyCsvFile,
  SurveyDetail,
  SurveyListRequest,
  SurveyListResponse,
  SurveyPurgeResult,
  SurveyQuestion,
  SurveyResetResult,
  SurveyRespondentErasure,
  SurveyResponseView,
  SurveyResponsesPage,
  SurveyResponsesRequest,
  SurveyResults,
  SurveyResultsCut,
  SurveyResultsFilter,
  SurveyUpdatePayload,
} from '../Types/Surveys.types';

type MockMode = 'off' | 'seeded' | 'empty' | 'error';

const STORAGE_KEY = 'survey-mock-mode';

const parseMode = (raw: string | null): MockMode | null => {
  const value = (raw ?? '').toLowerCase();

  if (value === 'seeded' || value === 'true') return 'seeded';
  if (value === 'empty') return 'empty';
  if (value === 'error') return 'error';
  if (value === 'off' || value === 'false') return 'off';

  return null;
};

/**
 * Off unless this tab asked for it.
 *
 * `?surveyMock=` wins and is remembered for the tab; anything unrecognised — including nothing at
 * all — leaves the service pointing at the BFF. Wrapped because `sessionStorage` throws in a
 * privacy-restricted context, and a demo switch must never be what breaks the screen.
 */
const readMode = (): MockMode => {
  try {
    const fromUrl = parseMode(new URLSearchParams(window.location.search).get('surveyMock'));

    if (fromUrl) {
      window.sessionStorage.setItem(STORAGE_KEY, fromUrl);

      return fromUrl;
    }

    return parseMode(window.sessionStorage.getItem(STORAGE_KEY)) ?? 'off';
  } catch {
    return 'off';
  }
};

const MODE: MockMode = readMode();

export const isSurveyMockEnabled = (): boolean => MODE !== 'off';

/** What the fake endpoints answer with, so a loading state is actually visible. */
const LATENCY_MS = 240;

const delay = <T>(value: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), LATENCY_MS);
  });

const fail = (): Promise<never> =>
  new Promise((_resolve, reject) => {
    setTimeout(() => reject(new Error('Falha simulada do mock de pesquisas')), LATENCY_MS);
  });

/** `03-fixtures/survey-completo.json`, with the admin fields the panel reads. */
const SEEDED_QUESTIONS: SurveyQuestion[] = [
  {
    id: 'q_1',
    key: 'nota_geral',
    type: 'RATING',
    step: 1,
    order: 0,
    isRequired: true,
    label: 'How would you rate the new menu?',
    helpText: null,
    config: { min: 1, max: 5, labels: { '1': 'Very bad', '5': 'Very good' } },
    options: [],
    rows: [],
    translations: [
      {
        language: 'pt',
        label: 'Que nota você dá para o novo menu?',
        config: { labels: { '1': 'Muito ruim', '5': 'Muito boa' } },
      },
      {
        language: 'es',
        label: '¿Qué nota le das al nuevo menú?',
        config: { labels: { '1': 'Muy malo', '5': 'Muy bueno' } },
      },
    ],
  },
  {
    id: 'q_2',
    key: 'ficou_mais_facil',
    type: 'BOOLEAN',
    step: 1,
    order: 1,
    isRequired: true,
    label: 'Is it easier to find what you are looking for?',
    helpText: null,
    config: { trueLabel: 'Yes', falseLabel: 'No' },
    options: [],
    rows: [],
    translations: [
      { language: 'pt', label: 'Ficou mais fácil encontrar o que você procura?' },
      { language: 'es', label: '¿Es más fácil encontrar lo que buscas?' },
    ],
  },
  {
    id: 'q_3',
    key: 'o_que_mais_usa',
    type: 'MULTI_CHOICE',
    step: 2,
    order: 0,
    isRequired: true,
    label: 'Which sections do you use the most?',
    helpText: 'Pick up to 3.',
    config: { minSelections: 1, maxSelections: 3 },
    options: [
      {
        id: 'opt_1',
        value: 'monitoramento',
        label: 'Monitoring & Quality',
        order: 0,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Monitoramento & Qualidade' },
          { language: 'es', label: 'Monitoreo y Calidad' },
        ],
      },
      {
        id: 'opt_2',
        value: 'projecao',
        label: 'Projection & Analytics',
        order: 1,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Projeção & Analytics' },
          { language: 'es', label: 'Proyección y Analytics' },
        ],
      },
      {
        id: 'opt_3',
        value: 'exportacao',
        label: 'Data Export',
        order: 2,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Exportação de Dados' },
          { language: 'es', label: 'Exportación de Datos' },
        ],
      },
      {
        id: 'opt_4',
        value: 'ativos',
        label: 'Assets',
        order: 3,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Ativos' },
          { language: 'es', label: 'Activos' },
        ],
      },
      {
        id: 'opt_5',
        value: 'outro',
        label: 'Another one',
        order: 4,
        isExclusive: false,
        allowsFreeText: true,
        translations: [
          { language: 'pt', label: 'Outra' },
          { language: 'es', label: 'Otra' },
        ],
      },
      {
        id: 'opt_6',
        value: 'nenhuma',
        label: 'None of them',
        order: 5,
        isExclusive: true,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Nenhuma delas' },
          { language: 'es', label: 'Ninguna de ellas' },
        ],
      },
    ],
    rows: [],
    translations: [
      { language: 'pt', label: 'Quais seções você mais usa?', helpText: 'Escolha até 3.' },
      { language: 'es', label: '¿Qué secciones usas más?', helpText: 'Elige hasta 3.' },
    ],
  },
  {
    id: 'q_4',
    key: 'secao_dificil',
    type: 'SINGLE_CHOICE',
    step: 2,
    order: 1,
    isRequired: false,
    label: 'Which section was hardest to find?',
    helpText: null,
    config: {},
    options: [
      {
        id: 'opt_7',
        value: 'monitoramento',
        label: 'Monitoring & Quality',
        order: 0,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Monitoramento & Qualidade' },
          { language: 'es', label: 'Monitoreo y Calidad' },
        ],
      },
      {
        id: 'opt_8',
        value: 'desenvolvedor',
        label: 'Developer Portal',
        order: 1,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Portal do Desenvolvedor' },
          { language: 'es', label: 'Portal del Desarrollador' },
        ],
      },
      {
        id: 'opt_9',
        value: 'nenhuma',
        label: 'None, I found everything',
        order: 2,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Nenhuma, achei tudo' },
          { language: 'es', label: 'Ninguna, encontré todo' },
        ],
      },
    ],
    rows: [],
    translations: [
      { language: 'pt', label: 'Qual seção foi mais difícil de encontrar?' },
      { language: 'es', label: '¿Qué sección fue más difícil de encontrar?' },
    ],
  },
  {
    id: 'q_5',
    key: 'avaliacao_menu',
    type: 'MATRIX',
    step: 3,
    order: 0,
    isRequired: true,
    label: 'Rate each aspect of the new menu',
    helpText: null,
    config: {},
    options: [
      {
        id: 'opt_m1',
        value: 'ruim',
        label: 'Bad',
        order: 0,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Ruim' },
          { language: 'es', label: 'Malo' },
        ],
      },
      {
        id: 'opt_m2',
        value: 'regular',
        label: 'Fair',
        order: 1,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Regular' },
          { language: 'es', label: 'Regular' },
        ],
      },
      {
        id: 'opt_m3',
        value: 'bom',
        label: 'Good',
        order: 2,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Bom' },
          { language: 'es', label: 'Bueno' },
        ],
      },
      {
        id: 'opt_m4',
        value: 'otimo',
        label: 'Great',
        order: 3,
        isExclusive: false,
        allowsFreeText: false,
        translations: [
          { language: 'pt', label: 'Ótimo' },
          { language: 'es', label: 'Excelente' },
        ],
      },
    ],
    rows: [
      {
        id: 'q_5a',
        key: 'organizacao',
        type: 'SINGLE_CHOICE',
        order: 0,
        isRequired: true,
        label: 'How the sections are organised',
        helpText: null,
        translations: [
          { language: 'pt', label: 'Organização das seções' },
          { language: 'es', label: 'Organización de las secciones' },
        ],
      },
      {
        id: 'q_5b',
        key: 'nomes_secoes',
        type: 'SINGLE_CHOICE',
        order: 1,
        isRequired: true,
        label: 'Section names',
        helpText: null,
        translations: [
          { language: 'pt', label: 'Nomes das seções' },
          { language: 'es', label: 'Nombres de las secciones' },
        ],
      },
      {
        id: 'q_5c',
        key: 'quantidade_itens',
        type: 'SINGLE_CHOICE',
        order: 2,
        isRequired: true,
        label: 'Number of items per section',
        helpText: null,
        translations: [
          { language: 'pt', label: 'Quantidade de itens por seção' },
          { language: 'es', label: 'Cantidad de elementos por sección' },
        ],
      },
    ],
    translations: [
      { language: 'pt', label: 'Avalie cada aspecto do novo menu' },
      { language: 'es', label: 'Evalúa cada aspecto del nuevo menú' },
    ],
  },
  {
    id: 'q_6',
    key: 'recomendaria',
    type: 'NPS',
    step: 4,
    order: 0,
    isRequired: true,
    label: 'How likely are you to recommend the new menu to a colleague?',
    helpText: null,
    config: { leftLabel: 'Not likely', rightLabel: 'Very likely' },
    options: [],
    rows: [],
    translations: [
      {
        language: 'pt',
        label: 'O quanto você recomendaria o novo menu a um colega?',
        config: { leftLabel: 'Nada provável', rightLabel: 'Muito provável' },
      },
      {
        language: 'es',
        label: '¿Qué tanto recomendarías el nuevo menú a un colega?',
        config: { leftLabel: 'Nada probable', rightLabel: 'Muy probable' },
      },
    ],
  },
  {
    id: 'q_7',
    key: 'cargo',
    type: 'SHORT_TEXT',
    step: 4,
    order: 1,
    isRequired: false,
    label: 'What is your role?',
    helpText: 'It helps us read your answer in context.',
    config: { maxLength: 120, placeholder: 'e.g. Operations analyst' },
    options: [],
    rows: [],
    translations: [
      {
        language: 'pt',
        label: 'Qual seu cargo?',
        helpText: 'Ajuda a entender o contexto da sua resposta.',
        config: { placeholder: 'Ex.: Analista de operações' },
      },
      {
        language: 'es',
        label: '¿Cuál es tu cargo?',
        helpText: 'Ayuda a entender el contexto de tu respuesta.',
        config: { placeholder: 'Ej.: Analista de operaciones' },
      },
    ],
  },
  {
    id: 'q_8',
    key: 'comentario',
    type: 'LONG_TEXT',
    step: 5,
    order: 0,
    isRequired: false,
    label: 'Anything else you would like to tell us?',
    helpText: null,
    config: { maxLength: 1000, placeholder: 'Write here...' },
    options: [],
    rows: [],
    translations: [
      {
        language: 'pt',
        label: 'Quer comentar alguma coisa?',
        config: { placeholder: 'Escreva aqui...' },
      },
      {
        language: 'es',
        label: '¿Quieres comentar algo?',
        config: { placeholder: 'Escribe aquí...' },
      },
    ],
  },
];

const SEEDED_SURVEY: SurveyDetail = {
  id: 'srv_abc123',
  tenantSlug: 'group_link_one',
  slug: 'satisfacao-mdm',
  version: 1,
  draftVersion: null,
  workingVersion: 1,
  reaskFromVersion: 1,
  title: 'How is your experience with the new menu?',
  description: 'It takes less than 2 minutes and helps decide the next steps.',
  layout: 'ONE_PER_STEP',
  trigger: 'AUTO',
  priority: 10,
  isActive: true,
  opensAt: '2026-10-01T00:00:00.000Z',
  closesAt: '2026-11-30T23:59:00.000Z',
  snoozeDays: 7,
  page: null,
  useCase: null,
  requiresOptInFeature: { id: 'oif_mdm_menu', key: 'mdm-menu', name: 'Novo menu MDM' },
  requiredOptInState: 'ENABLED',
  delayAmount: 7,
  delayUnit: 'DAYS',
  delayAnchor: 'LAST_ENABLED_AT',
  eligibleOptInFrom: null,
  maxDaysSinceAnchor: null,
  approvalStatus: 'APPROVED',
  reviewStatus: 'APPROVED',
  createdBy: { id: 'usr_1', name: 'Victor Perricci', email: 'v.perricci@grouplinkone.com' },
  approvedBy: { id: 'usr_2', name: 'Alguém do time', email: null },
  submittedAt: '2026-09-29T10:00:00.000Z',
  approvedAt: '2026-09-30T09:00:00.000Z',
  rejectedAt: null,
  rejectionReason: null,
  createdAt: '2026-09-28T12:00:00.000Z',
  updatedAt: '2026-10-04T09:31:00.000Z',
  supportedLanguages: ['en', 'pt', 'es'],
  translations: [
    {
      language: 'pt',
      title: 'Como está sua experiência com o novo menu?',
      description: 'Leva menos de 2 minutos e ajuda a decidir os próximos passos.',
    },
    {
      language: 'es',
      title: '¿Cómo es tu experiencia con el nuevo menú?',
      description: 'Toma menos de 2 minutos y ayuda a decidir los próximos pasos.',
    },
  ],
  responseCount: 610,
  questions: SEEDED_QUESTIONS,
};

/** `03-fixtures/results.json`, verbatim. */
const SEEDED_RESULTS: SurveyResults = {
  surveyId: 'srv_abc123',
  slug: 'satisfacao-mdm',
  version: 1,
  eligibilityScope: 'OPT_IN',
  totalEligible: 610,
  totalStarted: 610,
  totalSubmitted: 214,
  totalDismissed: 38,
  totalInProgress: 358,
  responseRate: 0.351,
  avgDurationMs: 161200,
  funnel: [
    {
      step: 1,
      reached: 610,
      avgMsAll: 12400,
      avgMsCompleted: 11800,
      avgMsAbandoned: 8200,
      dropped: 70,
    },
    {
      step: 2,
      reached: 540,
      avgMsAll: 31800,
      avgMsCompleted: 30100,
      avgMsAbandoned: 134000,
      dropped: 149,
    },
    {
      step: 3,
      reached: 391,
      avgMsAll: 74000,
      avgMsCompleted: 71200,
      avgMsAbandoned: 98000,
      dropped: 102,
    },
    {
      step: 4,
      reached: 289,
      avgMsAll: 21000,
      avgMsCompleted: 20400,
      avgMsAbandoned: 31000,
      dropped: 41,
    },
    {
      step: 5,
      reached: 248,
      avgMsAll: 22000,
      avgMsCompleted: 21800,
      avgMsAbandoned: 26000,
      dropped: 34,
    },
  ],
  questions: [
    {
      key: 'nota_geral',
      type: 'RATING',
      label: 'Que nota você dá para o novo menu?',
      answered: 214,
      average: 4.2,
      distribution: [
        { value: 1, count: 3 },
        { value: 2, count: 9 },
        { value: 3, count: 31 },
        { value: 4, count: 75 },
        { value: 5, count: 96 },
      ],
    },
    {
      key: 'ficou_mais_facil',
      type: 'BOOLEAN',
      label: 'Ficou mais fácil encontrar?',
      answered: 214,
      trueCount: 171,
      falseCount: 43,
      truePercent: 0.799,
    },
    {
      key: 'o_que_mais_usa',
      type: 'MULTI_CHOICE',
      label: 'Quais seções você mais usa?',
      answered: 210,
      options: [
        { value: 'monitoramento', label: 'Monitoramento & Qualidade', count: 152, percent: 0.724 },
        { value: 'exportacao', label: 'Exportação de Dados', count: 118, percent: 0.562 },
        { value: 'projecao', label: 'Projeção & Analytics', count: 87, percent: 0.414 },
        { value: 'ativos', label: 'Ativos', count: 64, percent: 0.305 },
        { value: 'outro', label: 'Outra', count: 11, percent: 0.052 },
        { value: 'nenhuma', label: 'Nenhuma delas', count: 2, percent: 0.01 },
      ],
    },
    {
      key: 'avaliacao_menu',
      type: 'MATRIX',
      label: 'Avalie cada aspecto do novo menu',
      answered: 208,
      scale: [
        { value: 'ruim', label: 'Ruim' },
        { value: 'regular', label: 'Regular' },
        { value: 'bom', label: 'Bom' },
        { value: 'otimo', label: 'Ótimo' },
      ],
      rows: [
        {
          key: 'organizacao',
          label: 'Organização das seções',
          type: 'SINGLE_CHOICE',
          answered: 208,
          average: 3.4,
          distribution: [
            { value: 'ruim', count: 6 },
            { value: 'regular', count: 21 },
            { value: 'bom', count: 71 },
            { value: 'otimo', count: 110 },
          ],
        },
        {
          key: 'nomes_secoes',
          label: 'Nomes das seções',
          type: 'SINGLE_CHOICE',
          answered: 208,
          average: 2.6,
          distribution: [
            { value: 'ruim', count: 28 },
            { value: 'regular', count: 62 },
            { value: 'bom', count: 79 },
            { value: 'otimo', count: 39 },
          ],
        },
        {
          key: 'quantidade_itens',
          label: 'Quantidade de itens por seção',
          type: 'SINGLE_CHOICE',
          answered: 208,
          average: 3.3,
          distribution: [
            { value: 'ruim', count: 9 },
            { value: 'regular', count: 26 },
            { value: 'bom', count: 76 },
            { value: 'otimo', count: 97 },
          ],
        },
      ],
    },
    {
      key: 'recomendaria',
      type: 'NPS',
      label: 'O quanto recomendaria a um colega?',
      answered: 214,
      score: 31,
      promoters: 108,
      passives: 65,
      detractors: 41,
      distribution: [
        { value: 0, count: 2 },
        { value: 1, count: 0 },
        { value: 2, count: 0 },
        { value: 3, count: 0 },
        { value: 4, count: 0 },
        { value: 5, count: 0 },
        { value: 6, count: 39 },
        { value: 7, count: 30 },
        { value: 8, count: 35 },
        { value: 9, count: 52 },
        { value: 10, count: 56 },
      ],
    },
    { key: 'cargo', type: 'SHORT_TEXT', label: 'Qual seu cargo?', answered: 168 },
    { key: 'comentario', type: 'LONG_TEXT', label: 'Quer comentar alguma coisa?', answered: 112 },
  ],
  optIn: {
    featureKey: 'mdm-menu',
    featureName: 'Novo menu MDM',
    everEnabled: 508,
    enabledNow: 412,
    reverted: 96,
    revertRate: 0.189,
    respondentsWhoReverted: 31,
  },
};

const COMMENT_TEXTS = [
  'Faltam filtros salvos.',
  'Achei os nomes das seções pouco óbvios.',
  'Muito melhor que o anterior, só senti falta do atalho de exportação.',
  'Demorei para achar o portal do desenvolvedor.',
  'Exportação em lote',
];

/**
 * The exit questionnaire of the same feature, so the two cards sit in one group on the listing.
 *
 * It is the whole point of the feature level: this and `satisfacao-mdm` ask about `mdm-menu` at two
 * different moments — one to whoever is using it, one to whoever is leaving — and the fixture has to
 * show that pair, not two unrelated surveys.
 */
const SEEDED_EXIT_SURVEY: SurveyDetail = {
  ...structuredClone(SEEDED_SURVEY),
  id: 'srv_saida01',
  slug: 'saida-menu-mdm',
  title: 'Thanks for trying the new menu',
  description: 'Can you tell us what did not work?',
  trigger: 'ON_OPT_OUT',
  priority: 0,
  requiredOptInState: 'DISABLED',
  delayAmount: null,
  delayUnit: null,
  delayAnchor: null,
  opensAt: null,
  closesAt: null,
  snoozeDays: null,
  createdAt: '2026-10-02T12:00:00.000Z',
  updatedAt: '2026-10-02T12:00:00.000Z',
  translations: [
    {
      language: 'pt',
      title: 'Obrigada por experimentar o novo menu',
      description: 'Pode nos contar o que não funcionou?',
    },
    {
      language: 'es',
      title: 'Gracias por probar el nuevo menú',
      description: '¿Puedes contarnos qué no funcionó?',
    },
  ],
  responseCount: 17,
};

/**
 * What the exit questionnaire collected, and it is deliberately NOT a copy of `SEEDED_RESULTS`.
 *
 * The adoption screen embeds these numbers right under cards that count people, so the fixture is
 * built so the two units cannot be mistaken for each other: **13 responses from 11 distinct
 * people**, because two of them left twice and were asked twice (R33). A fixture where every
 * response came from its own person would let the panel ship without the case it exists to survive.
 *
 * Three questions, the ones the UX wrote for the MDM menu: the reason (radio), what was hardest to
 * find (free text) and whether they would try again (radio).
 */
const SEEDED_EXIT_RESULTS: SurveyResults = {
  surveyId: 'srv_saida01',
  slug: 'saida-menu-mdm',
  version: 1,
  eligibilityScope: 'OPT_IN',
  totalEligible: 96,
  totalStarted: 17,
  totalSubmitted: 13,
  totalDismissed: 3,
  totalInProgress: 1,
  responseRate: 0.135,
  avgDurationMs: 42100,
  funnel: [
    {
      step: 1,
      reached: 17,
      avgMsAll: 21400,
      avgMsCompleted: 19800,
      avgMsAbandoned: 33200,
      dropped: 4,
    },
  ],
  questions: [
    {
      key: 'motivo_saida',
      type: 'SINGLE_CHOICE',
      label: 'Qual o principal motivo para voltar ao menu anterior?',
      answered: 13,
      options: [
        {
          value: 'nao_encontrei_item',
          label: 'Não encontrei um item que precisava',
          count: 5,
          percent: 0.385,
        },
        {
          value: 'organizacao_confusa',
          label: 'A nova organização não fez sentido para mim',
          count: 3,
          percent: 0.231,
        },
        { value: 'prefiro_conhecido', label: 'Prefiro o que já conheço', count: 2, percent: 0.154 },
        {
          value: 'sem_tempo_adaptar',
          label: 'Estava com pressa e não tive tempo de me adaptar',
          count: 1,
          percent: 0.077,
        },
        {
          value: 'problema_ou_bug',
          label: 'Encontrei algum problema ou bug',
          count: 1,
          percent: 0.077,
        },
        { value: 'outro', label: 'Outro motivo', count: 1, percent: 0.077 },
      ],
    },
    {
      key: 'item_dificil_encontrar',
      type: 'LONG_TEXT',
      label: 'Qual item ficou mais difícil de encontrar?',
      answered: 9,
    },
    {
      key: 'tentaria_de_novo',
      type: 'SINGLE_CHOICE',
      label: 'Se melhorarmos isso, você tentaria o novo menu de novo?',
      answered: 12,
      options: [
        { value: 'sim', label: '✅ Sim, com certeza', count: 7, percent: 0.583 },
        { value: 'talvez', label: '🤔 Talvez', count: 4, percent: 0.333 },
        { value: 'provavelmente_nao', label: '❌ Provavelmente não', count: 1, percent: 0.083 },
      ],
    },
  ],
  optIn: {
    featureKey: 'mdm-menu',
    featureName: 'Novo menu MDM',
    everEnabled: 508,
    enabledNow: 412,
    reverted: 96,
    revertRate: 0.189,
    respondentsWhoReverted: 11,
  },
};

/** What people wrote on the way out — the half of the answer the aggregates never carry. */
const EXIT_COMMENT_TEXTS = [
  'O portal do desenvolvedor. Procurei em Configurações e não estava lá.',
  'Relatórios agendados — sumiram do lugar que eu conhecia.',
  'Não achei a lista de dispositivos, só a de grupos.',
  'A exportação em lote. Levei uns cinco minutos e desisti.',
  'Os alarmes estavam em duas seções diferentes e eu nunca sabia qual abrir.',
];

const store: { surveys: SurveyDetail[] } = {
  surveys:
    MODE === 'seeded' ? [structuredClone(SEEDED_SURVEY), structuredClone(SEEDED_EXIT_SURVEY)] : [],
};

const findOrThrow = (id: string): SurveyDetail => {
  const survey = store.surveys.find(item => item.id === id);

  if (!survey) throw new Error('Pesquisa não encontrada');

  return survey;
};

const withoutQuestions = (survey: SurveyDetail): Survey => {
  const { questions: _questions, ...rest } = structuredClone(survey);

  return rest;
};

export const mockListSurveys = (params: SurveyListRequest = {}): Promise<SurveyListResponse> => {
  if (MODE === 'error') return fail();

  /*
    `includeDeleted` ADDS the deleted surveys to the live ones — it does not swap the listing for
    them. Getting it backwards here would make the screen look right against the mock and wrong
    against the BFF, which is the one failure a fixture must not produce.
  */
  const data = store.surveys
    .filter(survey => !params.tenantSlug || survey.tenantSlug === params.tenantSlug)
    .filter(survey => params.includeDeleted || !survey.deletedAt)
    .filter(survey => !params.trigger || survey.trigger === params.trigger)
    /*
      The exit-questionnaire panel of an opt-in feature asks by this pair, so the fixture has to
      honour it: answering the whole list would make that panel claim every survey of the tenant
      belongs to one feature.
    */
    .filter(
      survey =>
        !params.requiresOptInFeatureId ||
        survey.requiresOptInFeature?.id === params.requiresOptInFeatureId,
    )
    .map(survey => withoutQuestions(survey));

  return delay({ data, total: data.length, page: 1, limit: 100, totalPages: data.length ? 1 : 0 });
};

export const mockGetSurvey = (id: string): Promise<SurveyDetail> => {
  if (MODE === 'error') return fail();

  return delay(structuredClone(findOrThrow(id)));
};

export const mockCreateSurvey = (payload: SurveyCreatePayload): Promise<Survey> => {
  if (MODE === 'error') return fail();

  /*
    The conflict a deleted row raises is reproduced, because it is the dead end this whole feature
    exists to close: without it the restore offer on the create screen could only ever be seen
    against the real backend.
  */
  const holder = store.surveys.find(
    survey => survey.slug === payload.slug && survey.tenantSlug === payload.tenantSlug,
  );

  if (holder?.deletedAt) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new SurveySlugHeldByDeletedError(
            `The slug '${payload.slug}' belongs to a survey of tenant '${payload.tenantSlug}' that was deleted on ${holder.deletedAt}.`,
            holder.id,
            holder.deletedAt ?? null,
          ),
        );
      }, LATENCY_MS);
    });
  }

  const now = new Date().toISOString();
  const created: SurveyDetail = {
    id: `srv_${payload.slug}_${store.surveys.length + 1}`,
    tenantSlug: payload.tenantSlug,
    slug: payload.slug,
    version: 1,
    draftVersion: null,
    workingVersion: 1,
    reaskFromVersion: 1,
    title: payload.title,
    description: payload.description ?? null,
    layout: payload.layout ?? 'SINGLE_PAGE',
    trigger: payload.trigger ?? 'AUTO',
    priority: payload.priority ?? 0,
    isActive: payload.isActive ?? true,
    opensAt: payload.opensAt ?? null,
    closesAt: payload.closesAt ?? null,
    snoozeDays: payload.snoozeDays ?? null,
    page: null,
    useCase: null,
    requiresOptInFeature: payload.requiresOptInFeatureId
      ? { id: payload.requiresOptInFeatureId, key: payload.requiresOptInFeatureId, name: '' }
      : null,
    requiredOptInState: payload.requiredOptInState ?? null,
    delayAmount: payload.delayAmount ?? null,
    delayUnit: payload.delayUnit ?? null,
    delayAnchor: payload.delayAnchor ?? null,
    eligibleOptInFrom: payload.eligibleOptInFrom ?? null,
    maxDaysSinceAnchor: payload.maxDaysSinceAnchor ?? null,
    approvalStatus: 'DRAFT',
    reviewStatus: 'DRAFT',
    submittedAt: null,
    approvedAt: null,
    rejectedAt: null,
    rejectionReason: null,
    createdAt: now,
    updatedAt: now,
    supportedLanguages: ['en', 'pt', 'es'],
    translations: payload.translations ?? [],
    responseCount: 0,
    questions: [],
  };

  store.surveys = [...store.surveys, created];

  return delay(withoutQuestions(created));
};

export const mockUpdateSurvey = (id: string, payload: SurveyUpdatePayload): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const current = findOrThrow(id);

  /*
    R44 — swapping the trigger on a survey that has answers is refused, and the same trigger coming
    back is not a swap: the builder PATCHes the whole form, so refusing what did not change would
    break every window or priority edit. Reproduced here because the screen unlocks the field on a
    count it read earlier, and the race between that read and this write is exactly what the toast
    exists for — against the mock it is the only way to see it.
  */
  const requestedTrigger = payload.trigger;

  if (
    requestedTrigger !== undefined &&
    requestedTrigger !== current.trigger &&
    current.responseCount > 0
  ) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new SurveyTriggerLockedError(current.responseCount, current.trigger, requestedTrigger),
        );
      }, LATENCY_MS);
    });
  }

  const updated: SurveyDetail = {
    ...current,
    ...payload,
    description: payload.description ?? current.description,
    opensAt: payload.opensAt ?? current.opensAt,
    closesAt: payload.closesAt ?? current.closesAt,
    snoozeDays: payload.snoozeDays ?? current.snoozeDays,
    translations: payload.translations ?? current.translations,
    updatedAt: new Date().toISOString(),
  };

  store.surveys = store.surveys.map(survey => (survey.id === id ? updated : survey));

  return delay(withoutQuestions(updated));
};

/** Soft, like the BFF: the row stays and keeps holding its slug, which is the whole dead end. */
export const mockDeleteSurvey = (id: string): Promise<void> => {
  if (MODE === 'error') return fail();

  findOrThrow(id);
  const deletedAt = new Date().toISOString();
  store.surveys = store.surveys.map(survey =>
    survey.id === id ? { ...survey, deletedAt } : survey,
  );

  return delay(undefined);
};

/**
 * The undo, including the `409` of a survey that is not deleted — and the deactivation.
 *
 * `isActive` comes back `false` while `approvalStatus` is kept, exactly as the BFF does it. A mock
 * that returned it active would hide the one thing the screen has to explain afterwards.
 */
export const mockRestoreSurvey = (id: string): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const current = findOrThrow(id);

  if (!current.deletedAt) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('Esta pesquisa não está excluída — talvez alguém já a tenha restaurado.'));
      }, LATENCY_MS);
    });
  }

  const restored: SurveyDetail = { ...current, deletedAt: null, isActive: false };
  store.surveys = store.surveys.map(survey => (survey.id === id ? restored : survey));

  return delay(withoutQuestions(restored));
};

/**
 * Saving the questionnaire, including the `409` the version dialog exists for.
 *
 * The seeded survey has 610 responses, so the mock refuses the first save exactly as the BFF
 * does — otherwise the dialog could only ever be seen against the real backend.
 */
export const mockSaveSurveyQuestions = (
  id: string,
  payload: SaveQuestionsPayload,
): Promise<SurveyDetail> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);

  if (survey.responseCount > 0 && survey.draftVersion === null) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new SurveyVersionLockedError(
            'Survey has responses; structural edits require a new version.',
            survey.responseCount,
          ),
        );
      }, LATENCY_MS);
    });
  }

  const questions: SurveyQuestion[] = payload.questions.map((question, index) => ({
    id: question.id ?? `q_new_${index}`,
    key: question.key ?? `question_${index + 1}`,
    type: question.type,
    label: question.label,
    helpText: question.helpText ?? null,
    isRequired: question.isRequired ?? false,
    step: question.step ?? 1,
    order: question.order,
    config: question.config ?? {},
    options: (question.options ?? []).map((option, optionIndex) => ({
      id: `opt_new_${index}_${optionIndex}`,
      value: option.value,
      label: option.label,
      order: option.order,
      isExclusive: option.isExclusive ?? false,
      allowsFreeText: option.allowsFreeText ?? false,
      translations: option.translations,
    })),
    rows: (question.rows ?? []).map((row, rowIndex) => ({
      id: row.id ?? `row_new_${index}_${rowIndex}`,
      key: row.key ?? `row_${rowIndex + 1}`,
      type: row.type,
      label: row.label,
      helpText: row.helpText ?? null,
      isRequired: row.isRequired ?? false,
      order: row.order,
      translations: row.translations,
    })),
    translations: question.translations,
  }));

  const updated: SurveyDetail = { ...survey, questions, updatedAt: new Date().toISOString() };
  store.surveys = store.surveys.map(item => (item.id === id ? updated : item));

  return delay(structuredClone(updated));
};

export const mockSubmitSurvey = (id: string): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const updated: SurveyDetail = {
    ...survey,
    reviewStatus: 'PENDING_APPROVAL',
    approvalStatus: survey.draftVersion === null ? 'PENDING_APPROVAL' : survey.approvalStatus,
    submittedAt: new Date().toISOString(),
  };

  store.surveys = store.surveys.map(item => (item.id === id ? updated : item));

  return delay(withoutQuestions(updated));
};

export const mockApproveSurvey = (id: string): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const updated: SurveyDetail = {
    ...survey,
    approvalStatus: 'APPROVED',
    reviewStatus: 'APPROVED',
    approvedAt: new Date().toISOString(),
    version: survey.draftVersion ?? survey.version,
    draftVersion: null,
    workingVersion: survey.draftVersion ?? survey.version,
  };

  store.surveys = store.surveys.map(item => (item.id === id ? updated : item));

  return delay(withoutQuestions(updated));
};

export const mockRejectSurvey = (id: string, reason: string): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const updated: SurveyDetail = {
    ...survey,
    reviewStatus: 'REJECTED',
    approvalStatus: survey.draftVersion === null ? 'REJECTED' : survey.approvalStatus,
    rejectedAt: new Date().toISOString(),
    rejectionReason: reason,
  };

  store.surveys = store.surveys.map(item => (item.id === id ? updated : item));

  return delay(withoutQuestions(updated));
};

/**
 * Throwing the draft away, including the `409` of a survey that has none (R41).
 *
 * The refusal is reproduced because it is the one thing the screen has to be able to show: without
 * it the button would look harmless against fixtures and the pointer to `rollback-version` — the
 * whole reason the message is long — would never be read.
 *
 * It answers the **whole survey**, as the BFF does, because `draftVersion: null` in that payload is
 * the confirmation the caller reseeds the form from.
 */
export const mockDiscardSurveyDraft = (id: string): Promise<SurveyDetail> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);

  if (survey.draftVersion === null) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `Survey has no draft version open, so there is nothing to discard. If the draft was just approved it is no longer a draft: it is the delivered version ${survey.version}, and undoing that is POST /v1/surveys/${id}/rollback-version, which deletes nothing. Nothing was changed.`,
          ),
        );
      }, LATENCY_MS);
    });
  }

  /*
    Nothing of the review is written or erased here — that is the difference from `reject`, and a
    mock that stamped `rejectedAt` would hide exactly what R41 exists to keep distinguishable. Only
    `submittedAt` is cleared, and only when the draft had been sent.
  */
  const discarded: SurveyDetail = {
    ...survey,
    draftVersion: null,
    workingVersion: survey.version,
    reviewStatus: survey.approvalStatus,
    submittedAt: survey.reviewStatus === 'PENDING_APPROVAL' ? null : survey.submittedAt,
  };

  store.surveys = store.surveys.map(item => (item.id === id ? discarded : item));

  return delay(discarded);
};

/**
 * Putting the survey back the way it was created, including the `400` the slug field exists for.
 *
 * The counters are answered from the store so the toast has real numbers to print — a mock that
 * returned zeros would let a screen that says "pronto" pass for one that says what happened.
 */
export const mockResetSurvey = (id: string, confirmSlug: string): Promise<SurveyResetResult> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);

  if (confirmSlug !== survey.slug) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `confirmSlug does not match the survey slug '${survey.slug}'. Nothing was deleted.`,
          ),
        );
      }, LATENCY_MS);
    });
  }

  const deletedResponses = survey.responseCount;
  const deletedVersions = Math.max(survey.version, survey.draftVersion ?? 0) - 1;

  const reset: SurveyDetail = {
    ...survey,
    version: 1,
    draftVersion: null,
    workingVersion: 1,
    reaskFromVersion: 1,
    responseCount: 0,
    reviewStatus: survey.approvalStatus,
  };

  store.surveys = store.surveys.map(item => (item.id === id ? reset : item));

  return delay({
    deletedResponses,
    deletedVersions,
    deletedQuestionEdits: 0,
    version: 1,
    draftVersion: null,
    reaskFromVersion: 1,
    // Unchanged, on purpose: resetting neither demotes nor approves.
    approvalStatus: survey.approvalStatus,
  });
};

export const mockBumpSurveyVersion = (id: string, reask: boolean): Promise<Survey> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const nextVersion = survey.version + 1;
  const updated: SurveyDetail = {
    ...survey,
    draftVersion: nextVersion,
    workingVersion: nextVersion,
    reaskFromVersion: reask ? nextVersion : survey.reaskFromVersion,
    reviewStatus: 'DRAFT',
  };

  store.surveys = store.surveys.map(item => (item.id === id ? updated : item));

  return delay(withoutQuestions(updated));
};

/**
 * The cut by one answer (R49), over a fixture that has no per-person answers to intersect.
 *
 * What it reproduces is the part the screen has to survive, and reproduces it exactly:
 *
 * - **only `questions[]` narrows.** The funnel, the rate, the eligible audience, the top counters,
 *   the average duration and the `optIn` block come back untouched, byte for byte;
 * - **`filter` carries both bases**, so every sentence the screen writes has its numbers;
 * - **a cut that catches nobody is a `200` with zeros**, reachable by cutting on an option nobody
 *   chose — the case the screen must call a result and not an error.
 *
 * What it does NOT reproduce is a real intersection: with no answer rows, the counts of the other
 * questions are scaled proportionally, so the shape is right and the individual numbers are a
 * stand-in. The averages and the NPS score are left alone — the screen prints them, it does not
 * compute them, and inventing a recomputed mean would be a second lie on top of the first.
 *
 * The four `400`s are not reproduced, on purpose: the control emits a question and an option of
 * the version on screen or nothing at all, so no click can form a cut the BFF would refuse.
 */
const applyMockCut = (results: SurveyResults, cut: SurveyResultsCut): SurveyResults => {
  const parent = results.questions.find(question => question.key === cut.filterQuestionKey);
  const chosen = parent?.options?.find(option => option.value === cut.filterOptionValue);
  const people = chosen?.count ?? 0;
  const base = results.totalSubmitted || 1;
  const share = people / base;

  const scale = (count: number): number => Math.round(count * share);

  return {
    ...results,
    questions: results.questions.map(question => {
      if (question.key === cut.filterQuestionKey) {
        /* The question being cut on answers itself: everybody in the group marked that option. */
        return {
          ...question,
          answered: people,
          options: (question.options ?? []).map(option => ({
            ...option,
            count: option.value === cut.filterOptionValue ? people : 0,
            percent: option.value === cut.filterOptionValue && people > 0 ? 1 : 0,
          })),
        };
      }

      const answered = Math.min(question.answered, people);

      return {
        ...question,
        answered,
        ...(question.options
          ? {
              options: question.options.map(option => ({
                ...option,
                count: scale(option.count),
                percent: answered > 0 ? scale(option.count) / answered : 0,
              })),
            }
          : {}),
        ...(question.distribution
          ? {
              distribution: question.distribution.map(entry => ({
                ...entry,
                count: scale(entry.count),
              })),
            }
          : {}),
        ...(question.rows
          ? {
              rows: question.rows.map(row => ({
                ...row,
                answered: Math.min(row.answered, people),
                distribution: row.distribution.map(entry => ({
                  ...entry,
                  count: scale(entry.count),
                })),
              })),
            }
          : {}),
        ...(question.trueCount === undefined
          ? {}
          : {
              trueCount: scale(question.trueCount),
              falseCount: scale(question.falseCount ?? 0),
              truePercent: answered > 0 ? scale(question.trueCount) / answered : 0,
            }),
      };
    }),
    filter: {
      questionKey: cut.filterQuestionKey,
      questionLabel: parent?.label ?? cut.filterQuestionKey,
      optionValue: cut.filterOptionValue,
      optionLabel: chosen?.label ?? cut.filterOptionValue,
      people,
      totalSubmitted: results.totalSubmitted,
    },
  };
};

export const mockGetSurveyResults = (
  id: string,
  version?: number,
  cut?: SurveyResultsCut,
): Promise<SurveyResults> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);

  if (survey.slug === SEEDED_EXIT_RESULTS.slug) {
    const exit = {
      ...structuredClone(SEEDED_EXIT_RESULTS),
      version: version ?? SEEDED_EXIT_RESULTS.version,
    };

    return delay(cut ? applyMockCut(exit, cut) : exit);
  }

  if (survey.slug !== SEEDED_SURVEY.slug) {
    return delay({
      surveyId: survey.id,
      slug: survey.slug,
      version: version ?? survey.version,
      eligibilityScope: survey.requiresOptInFeature ? 'OPT_IN' : 'EVERYONE',
      totalEligible: survey.requiresOptInFeature ? 0 : null,
      totalStarted: 0,
      totalSubmitted: 0,
      totalDismissed: 0,
      totalInProgress: 0,
      responseRate: survey.requiresOptInFeature ? 0 : null,
      avgDurationMs: null,
      funnel: [],
      questions: [],
    });
  }

  const seeded = { ...structuredClone(SEEDED_RESULTS), version: version ?? SEEDED_RESULTS.version };

  return delay(cut ? applyMockCut(seeded, cut) : seeded);
};

export const mockGetSurveyResponses = (
  id: string,
  params: SurveyResponsesRequest,
): Promise<SurveyResponsesPage> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const total = survey.slug === SEEDED_SURVEY.slug ? SEEDED_RESULTS.totalStarted : 0;
  const limit = params.limit ?? 25;
  const page = params.page ?? 1;
  const start = (page - 1) * limit;
  const count = Math.max(0, Math.min(limit, total - start));

  const responses = Array.from({ length: count }, (_item, index) => {
    const position = start + index;
    const status =
      position < SEEDED_RESULTS.totalSubmitted
        ? 'SUBMITTED'
        : position < SEEDED_RESULTS.totalSubmitted + SEEDED_RESULTS.totalDismissed
          ? 'DISMISSED'
          : 'IN_PROGRESS';

    return {
      id: `rsp_${position}`,
      platformUserId: 418000 + position,
      status: status as SurveyResponseView['status'],
      surveyVersion: params.version ?? 1,
      language: ['pt', 'en', 'es'][position % 3],
      daysSinceAnchor: 7 + (position % 34),
      stepsSeen: status === 'SUBMITTED' ? 5 : 1 + (position % 4),
      durationMs: status === 'SUBMITTED' ? 120000 + position * 97 : 20000 + position * 31,
      dismissCount: status === 'DISMISSED' ? 1 : 0,
      startedAt: '2026-10-08T14:19:00.000Z',
      submittedAt: status === 'SUBMITTED' ? '2026-10-08T14:22:00.000Z' : null,
      answers: [],
    };
  }).filter(response => !params.status || response.status === params.status);

  return delay({ responses, total, page, limit, totalPages: Math.ceil(total / limit) });
};

/**
 * Erasing everybody, including the `400` the slug field exists to prevent.
 *
 * The refusal is reproduced because it is the whole safety of this endpoint: a mock that accepted
 * any string would let the dialog be judged without its one real guard ever firing.
 */
export const mockPurgeSurvey = (id: string, confirmSlug: string): Promise<SurveyPurgeResult> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);

  if (confirmSlug !== survey.slug) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(new Error('O slug digitado não bate com o da pesquisa. Nada foi apagado.'));
      }, LATENCY_MS);
    });
  }

  const deletedResponses = survey.responseCount;
  store.surveys = store.surveys.map(entry =>
    entry.id === id ? { ...entry, responseCount: 0 } : entry,
  );

  return delay({ deletedResponses });
};

/**
 * Erasing one person, including the `404` the screen has a sentence for.
 *
 * The fixtures are generated per page rather than stored, so nothing here can actually be removed;
 * what it does reproduce is the pair of answers the screen has to tell apart — a `200` carrying how
 * many rows went, and the `404` of somebody who is not in this survey. The rule matches the
 * generator above: seeded ids start at `418000`.
 */
export const mockDeleteSurveyRespondent = (
  id: string,
  platformUserId: number,
): Promise<SurveyRespondentErasure> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const total = survey.slug === SEEDED_SURVEY.slug ? SEEDED_RESULTS.totalStarted : 0;
  const position = platformUserId - 418000;

  if (position < 0 || position >= total) {
    return new Promise((_resolve, reject) => {
      setTimeout(() => {
        reject(
          new Error(
            `Não há resposta de ${platformUserId} nesta pesquisa. Ou a pessoa nunca respondeu, ou alguém já apagou — recarregue a lista.`,
          ),
        );
      }, LATENCY_MS);
    });
  }

  return delay({ platformUserId, deletedResponses: 1, message: 'Responses deleted' });
};

/** The `filter` block of a cut, read off the same fixture `/results` aggregates. */
const mockCutFilter = (
  results: SurveyResults,
  params: Partial<SurveyResultsCut>,
): SurveyResultsFilter | undefined => {
  const { filterQuestionKey, filterOptionValue } = params;

  if (!filterQuestionKey || !filterOptionValue) return undefined;

  const parent = results.questions.find(question => question.key === filterQuestionKey);
  const chosen = parent?.options?.find(option => option.value === filterOptionValue);

  return {
    questionKey: filterQuestionKey,
    questionLabel: parent?.label ?? filterQuestionKey,
    optionValue: filterOptionValue,
    optionLabel: chosen?.label ?? filterOptionValue,
    people: chosen?.count ?? 0,
    totalSubmitted: results.totalSubmitted,
  };
};

export const mockGetSurveyComments = (
  id: string,
  params: SurveyCommentsRequest,
): Promise<SurveyCommentsPage> => {
  if (MODE === 'error') return fail();

  const survey = findOrThrow(id);
  const isExit = survey.slug === SEEDED_EXIT_RESULTS.slug;
  /* Nine answers to the free-text question plus the one box behind "Outro motivo" — the exit
     questionnaire's own count, not a slice of the other survey's 112. */
  const fullTotal = isExit ? 10 : survey.slug === SEEDED_SURVEY.slug ? 112 : 0;

  /*
    The same cut as `/results` (R49), over a fixture with no answer rows to intersect.

    What it reproduces is what the feed has to prove: the list gets **shorter**, and it comes back
    with the `filter` block, so the count beside it can say whose text this is. Which comments
    survive is "the first N" and nothing cleverer — there is no per-person answer here to match on,
    and pretending otherwise would put invented people behind real sentences.
  */
  const cutFilter = mockCutFilter(isExit ? SEEDED_EXIT_RESULTS : SEEDED_RESULTS, params);
  const total = cutFilter ? Math.min(fullTotal, cutFilter.people) : fullTotal;
  const limit = params.limit ?? 50;
  const page = params.page ?? 1;
  const start = (page - 1) * limit;
  const count = Math.max(0, Math.min(limit, total - start));

  const comments = Array.from({ length: count }, (_item, index) => {
    const position = start + index;
    const isOther = position % 9 === 0;

    if (isExit) {
      return {
        responseId: `rsp_saida_${position}`,
        /* Two ids repeat on purpose: the same person left twice and was asked twice (R33), which
           is why the panel says "respostas" where the cards above say "pessoas". */
        platformUserId: 418000 + (position % 8),
        questionKey: isOther ? 'motivo_saida' : 'item_dificil_encontrar',
        questionLabel: isOther
          ? 'Qual o principal motivo para voltar ao menu anterior?'
          : 'Qual item ficou mais difícil de encontrar?',
        questionType: (isOther ? 'SINGLE_CHOICE' : 'LONG_TEXT') as SurveyComment['questionType'],
        source: (isOther ? 'OTHER_TEXT' : 'ANSWER') as SurveyComment['source'],
        text: EXIT_COMMENT_TEXTS[position % EXIT_COMMENT_TEXTS.length],
        language: 'pt',
        submittedAt: '2026-10-09T09:14:00.000Z',
      };
    }

    return {
      responseId: `rsp_${position}`,
      platformUserId: 418000 + position,
      questionKey: isOther ? 'o_que_mais_usa' : 'comentario',
      questionLabel: isOther ? 'Quais seções você mais usa?' : 'Quer comentar alguma coisa?',
      questionType: (isOther ? 'MULTI_CHOICE' : 'LONG_TEXT') as SurveyComment['questionType'],
      source: (isOther ? 'OTHER_TEXT' : 'ANSWER') as SurveyComment['source'],
      text: COMMENT_TEXTS[position % COMMENT_TEXTS.length],
      language: 'pt',
      submittedAt: '2026-10-08T14:22:00.000Z',
    };
  }).filter(comment => !params.questionKey || comment.questionKey === params.questionKey);

  return delay({
    comments,
    total,
    page,
    limit,
    totalPages: Math.ceil(total / limit),
    ...(cutFilter ? { filter: cutFilter } : {}),
  });
};

export const mockExportSurveyCsv = (
  id: string,
  slug: string,
  version?: number,
): Promise<SurveyCsvFile> => {
  if (MODE === 'error') return fail();

  findOrThrow(id);

  // The BOM matters even in the mock: without it Excel on Windows reads UTF-8 as Latin-1, and
  // that is exactly the failure the download is supposed to prove does not happen.
  const csv =
    '﻿platformUserId,submittedAt,daysSinceAnchor,durationMs,nota_geral,o_que_mais_usa,o_que_mais_usa_other,organizacao,comentario\r\n' +
    '418492,2026-10-08T14:22:00Z,12,161200,5,"monitoramento;outro","Exportação em lote",bom,"Faltam filtros salvos."\r\n';

  return delay({
    filename: `${slug}-v${version ?? 1}.csv`,
    blob: new Blob([csv], { type: 'text/csv;charset=utf-8' }),
  });
};
